// SendToNexus.ts

import api, { API_PATHS } from "./api";
import { openNexusErrorModal } from "../store/overlay";
import { setNotification } from "../store/toastNotifications";
import { NexusSendResult, SendPlanToNexusBody } from "../types/apiTypes";
import { DragAndDropApi } from "./DragAndDropApi";
import { AxiosError, AxiosResponse } from "axios";
import { AppDispatch } from "../store";
import labels from "../utils/labels";

export class SendPlanToNexus {
  static handleResponse(
    responseOrError:
      | AxiosResponse<NexusSendResult, any>
      | AxiosError<NexusSendResult>,
    dispatch: AppDispatch,
  ) {
    let endpointResult: { hasCalled: boolean; success: boolean } = {
      hasCalled: false,
      success: false,
    };

    // Handle Axios errors
    if (responseOrError instanceof Error || "isAxiosError" in responseOrError) {
      const error = responseOrError as AxiosError<NexusSendResult>;
      endpointResult = { hasCalled: true, success: false };

      if (error.response) {
        const response = error.response;
        const status = response.status;

        // Handle 409 conflict separately
        if (response.data && response.data.failedTasks.length > 0) {
          dispatch(
            openNexusErrorModal({
              failedTasks: response.data.failedTasks,
              message: response.data.message,
            }),
          );
          return endpointResult;
        }

        const message = `${labels.nexusExportErrorGeneral} (${labels.error}: ${status})`;

        dispatch(
          setNotification({
            message,
            type: "send_to_nexus",
          }),
        );
      } else if (error.request) {
        dispatch(
          setNotification({
            message: labels.nexusServerConnectionError,
            type: "send_to_nexus",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: labels.nexusExportErrorGeneral,
            type: "send_to_nexus",
          }),
        );
      }

      return endpointResult;
    }

    const response = responseOrError as AxiosResponse<NexusSendResult, any>;

    if (response.status === 200 && response.data) {
      const result = response.data;

      if (result.success) {
        endpointResult = { hasCalled: true, success: true };
        dispatch(
          setNotification({
            message: labels.nexusExportSuccess,
            type: "send_to_nexus",
            variant: "success",
          }),
        );
      } else {
        // Failure with possible task details
        endpointResult = { hasCalled: true, success: false };

        if (
          "failedTasks" in result &&
          result.failedTasks &&
          result.failedTasks.length > 0
        ) {
          dispatch(
            openNexusErrorModal({
              failedTasks: result.failedTasks,
              message: result.message,
            }),
          );
        } else {
          dispatch(
            openNexusErrorModal({
              message: result.message || labels.nexusExportErrorGeneral,
            }),
          );
        }
      }
    } else if (response.status === 409 && response.data) {
      //  Conflict case
      const result = response.data;
      endpointResult = { hasCalled: true, success: false };
      dispatch(
        openNexusErrorModal({
          failedTasks: result.failedTasks,
          message: result.message,
        }),
      );
    } else {
      // Other errors
      endpointResult = { hasCalled: true, success: false };
      dispatch(
        setNotification({
          message: "Vi kunne ikke sende planen til Nexus",
          type: "send_to_nexus",
        }),
      );
    }

    return endpointResult;
  }

  static async sendOptimisedPlanToNexus({
    idScenario,
    force = false,
    forceSend,
    dispatch,
  }: {
    idScenario: number;
    force?: boolean;
    forceSend?: boolean;
    dispatch: AppDispatch;
  }) {
    let endpointResult: { hasCalled: boolean; success: boolean };
    try {
      const body: SendPlanToNexusBody = {
        idScenario,
        forceSend: forceSend || force,
      };

      const response = await api.post<NexusSendResult>(
        API_PATHS.postNexusPlan,
        body,
      );
      endpointResult = this.handleResponse(response, dispatch);
    } catch (error: any) {
      console.error("Error sending plan to Nexus:", error);
      endpointResult = this.handleResponse(error, dispatch);
    }
    return endpointResult;
  }

  static async saveCustomPlan(dispatch: AppDispatch) {
    let endpointResult: { hasCalled: boolean; success: boolean };

    try {
      const response = await DragAndDropApi.saveAndFinalize();
      endpointResult = { hasCalled: true, success: response.success };
      if (response.success) {
        dispatch(
          setNotification({
            message: labels.nexusExportSuccess,
            type: "send_to_nexus",
            variant: "success",
          }),
        );
      }
    } catch (error: any) {
      // Catch network/other exceptions
      endpointResult = { hasCalled: true, success: false };

      dispatch(
        setNotification({
          message: "Vi kunne ikke sende planen til Nexus",
          type: "send_to_nexus",
        }),
      );
    }
    return endpointResult;
  }
}
